/**
 * NodeSettingsDialog.tsx
 * カードの設定ダイアログ。中身は各カードの settings コンポーネントを受け取って描画する。
 */
import { CircleHelp, X } from 'lucide-react';
import { useEffect, useId, useRef, useState, type ComponentType } from 'react';
import { createPortal } from 'react-dom';
import { ProductTour, type ProductTourStep } from '@/components/ProductTour';
import type { AppNode, NodeKind } from '../types';

const inputTourDescriptions: Partial<Record<NodeKind, string[]>> = {
  function: ['実行する関数の名前を入力します。名前を付けると、その関数の処理を編集できるようになります。'],
  textEdit: ['次のカードへ渡す文字を入力します。'],
  sayText: [
    'ロボットが話すときの声の種類を選びます。',
    'ロボットが話す速さを調整します。',
    'ロボットが話す声の高さを調整します。',
  ],
  gesture: ['ロボットに実行させる動きを選びます。'],
  walking: ['ロボットに実行させる歩行モーションを選びます。'],
  customGesture: ['作成したジェスチャーから、ロボットに実行させる動きを選びます。'],
  dance: ['ロボットに踊らせるダンスを選びます。'],
  if: [
    '入力値と比較値を比べる演算子を選びます。',
    '入力値と比較する値を入力します。',
  ],
  switch: [
    '入力された内容と比較する1つ目のことばを入力します。',
    '入力された内容と比較する2つ目のことばを入力します。',
  ],
  counter: [
    'カウントを始める数を入力します。',
    '繰り返すたびに増やす数を入力します。',
    'カウントを終える数を入力します。',
  ],
  wait: ['次のカードを実行するまで待つ秒数を入力します。'],
  randomChoice: [
    'ランダムに選ぶ1つ目の候補を入力します。',
    'ランダムに選ぶ2つ目の候補を入力します。',
    'ランダムに選ぶ3つ目の候補を入力します。',
  ],
  keywordSwitch: [
    '入力された文章に含まれているか調べる1つ目のキーワードを入力します。',
    '入力された文章に含まれているか調べる2つ目のキーワードを入力します。',
  ],
  soundEffect: ['ロボットから鳴らす効果音を選びます。'],
  light: ['ロボットのライトを光らせる色を選びます。'],
};

const getInputTourSteps = (nodeType: NodeKind): ProductTourStep[] =>
  (inputTourDescriptions[nodeType] ?? []).map((description, index) => ({
    target: `[data-product-tour="card-input-${index + 1}"]`,
    description,
  }));

type NodeSettingsDialogProps = {
  node: AppNode;
  /** カード名（表示言語で切り替え済みの文字列） */
  title: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  content: ComponentType<{ node: any }>;
  onClose: () => void;
};

export function NodeSettingsDialog({ node, title, content: Content, onClose }: NodeSettingsDialogProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const [tourOpen, setTourOpen] = useState(false);
  const tourSteps = getInputTourSteps(node.type);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const firstControl = bodyRef.current?.querySelector<HTMLElement>(
      'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    (firstControl ?? closeButtonRef.current)?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (document.querySelector('.product-tour')) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...(panelRef.current?.querySelectorAll<HTMLElement>(
        'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [href], [tabindex]:not([tabindex="-1"])',
      ) ?? [])].filter((element) => element.getAttribute('aria-hidden') !== 'true');
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [onClose]);

  return createPortal(
    <div className="node-settings-dialog" role="presentation" onClick={onClose}>
      <div
        ref={panelRef}
        className="node-settings-dialog__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="node-settings-dialog__header">
          <img
            src={`/images/flow-cards/${node.type}.png`}
            alt=""
            aria-hidden="true"
            className="node-settings-dialog__icon"
          />
          <h2 id={titleId} className="node-settings-dialog__title">{title}のせってい</h2>
          <button
            type="button"
            onClick={() => setTourOpen(true)}
            aria-label={`${title}カードの使い方`}
            title="使い方"
            className="node-settings-dialog__tour"
          >
            <CircleHelp aria-hidden="true" />
          </button>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="せっていを閉じる"
            title="せっていを閉じる"
            className="node-settings-dialog__close"
          >
            <X aria-hidden="true" />
          </button>
        </header>
        <div ref={bodyRef} className="node-settings-dialog__body">
          <Content node={node} />
          <p className="node-settings-dialog__autosave">変更は自動保存されます</p>
        </div>
      </div>
      {tourOpen && <ProductTour steps={tourSteps} onClose={() => setTourOpen(false)} />}
    </div>,
    document.body,
  );
}
